export type TemplateId = "paper" | "timeline" | "gallery";
export type ExportQuality = "light" | "standard" | "archive";
export type AssetVariant = "original" | "preview" | "thumbnail";

export interface PhotoItem {
  id: string;
  assetId: string;
  title: string;
  caption: string;
  date?: string;
  location?: string;
  people: string[];
  order: number;
}

export interface PhotoFolioProject {
  schemaVersion: 1;
  id: string;
  title: string;
  subtitle: string;
  author: string;
  date: string;
  location: string;
  people: string[];
  coverAssetId?: string;
  items: PhotoItem[];
  template: TemplateId;
  exportQuality: ExportQuality;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string;
}

export interface AssetRecord {
  id: string;
  projectId: string;
  originalName: string;
  mimeType: string;
  width: number;
  height: number;
  size: number;
  capturedAt?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
}

export interface AssetFileRecord {
  id: string;
  projectId: string;
  assetId: string;
  variant: AssetVariant;
  blob: Blob;
}

export interface AlbumPage {
  id: string;
  assetId: string;
  title: string;
  caption: string;
  date?: string;
  location?: string;
}

export interface AlbumDocument {
  title: string;
  subtitle: string;
  date: string;
  location: string;
  template: TemplateId;
  coverAssetId?: string;
  pages: AlbumPage[];
}

export const makeId = (prefix: string) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

export function createProject(title = "未命名影集"): PhotoFolioProject {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: makeId("project"),
    title,
    subtitle: "把经过的光，留在这里。",
    author: "",
    date: "",
    location: "",
    people: [],
    items: [],
    template: "paper",
    exportQuality: "standard",
    createdAt: now,
    updatedAt: now,
    lastOpenedAt: now,
  };
}

export function normalizeProject(input: unknown): PhotoFolioProject {
  if (!input || typeof input !== "object") throw new Error("项目文件内容无效");
  const candidate = input as Partial<PhotoFolioProject>;
  if (candidate.schemaVersion !== 1 || !candidate.id || !Array.isArray(candidate.items)) {
    throw new Error("暂不支持此项目版本");
  }
  const now = new Date().toISOString();
  return {
    ...createProject(candidate.title || "导入的影集"),
    ...candidate,
    schemaVersion: 1,
    items: candidate.items.map((item, index) => ({
      id: item.id || makeId("item"),
      assetId: item.assetId,
      title: item.title || "",
      caption: item.caption || "",
      date: item.date,
      location: item.location,
      people: Array.isArray(item.people) ? item.people : [],
      order: index,
    })),
    updatedAt: now,
    lastOpenedAt: now,
  };
}

export function buildAlbumDocument(project: PhotoFolioProject): AlbumDocument {
  return {
    title: project.title,
    subtitle: project.subtitle,
    date: project.date,
    location: project.location,
    template: project.template,
    coverAssetId: project.coverAssetId || project.items[0]?.assetId,
    pages: [...project.items]
      .sort((a, b) => a.order - b.order)
      .map(({ id, assetId, title, caption, date, location }) => ({
        id,
        assetId,
        title,
        caption,
        date,
        location,
      })),
  };
}
